import React, { useState } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import { IContact, ContactType } from 'models';
import { Scope } from 'redux/reducers/authReducer';
import { useRemoveContact, useContacts, useSaveContact } from './hooks';
import { usePermission } from 'hooks';
import { ModalState } from 'components/organism/crud';

export const useContactActions = () => {
  const hasPermissionCreate = usePermission(Scope.ContactsCreate);
  const hasPermissionUpdate = usePermission(Scope.ContactsUpdate);
  const hasPermissionDelete = usePermission(Scope.ContactsDelete);

  const [modalState, setModalState] = useState<ModalState>(ModalState.Hidden);
  const [obj, setObj] = useState<IContact | undefined>(undefined);

  const saveHook = useSaveContact();
  const removeHook = useRemoveContact();
  const listHook = useContacts({
    defaultFilters: { type: ContactType.All, search: '' },
  });

  const newContact = () => {
    setObj(undefined);
    setModalState(ModalState.New);
  };

  const viewContact = (obj: IContact) => {
    setObj(obj);
    setModalState(ModalState.View);
  };

  const editContact = (obj: IContact) => {
    setObj(obj);
    setModalState(ModalState.Edit);
  };

  const closeModal = () => {
    setObj(undefined);
    setModalState(ModalState.Hidden);
    // TODO: Set object to undefined
  };

  return {
    permissions: {
      hasPermissionCreate,
      hasPermissionUpdate,
      hasPermissionDelete,
    },
    modal: {
      modalState,
      obj,
      newContact,
      viewContact,
      editContact,
      closeModal,
    },
    list: {
      hook: listHook,
    },
    newContact: {
      definition: {
        key: 'action-new',
        title: 'New',
        icon: null,
        type: 'primary',
        handler: () => {
          newContact();
        },
        canRender: () => hasPermissionCreate,
      },
    },
    editContact: {
      definition: {
        key: 'action-edit',
        title: 'Edit',
        icon: <EditOutlined />,
        handler: (obj: IContact) => {
          editContact(obj);
        },
        type: 'primary',
        canRender: () => {
          if (modalState === ModalState.New || modalState === ModalState.Edit) {
            return false;
          }
          return hasPermissionUpdate;
        },
      },
    },
    deleteContact: {
      hook: removeHook,
      handleOk: async () => {
        await removeHook.execute();
        removeHook.reset();

        closeModal();

        await listHook.reloadList();
      },
      handleCancel: async () => {
        removeHook.reset();
      },
      definition: {
        key: 'action-delete',
        title: 'Delete',
        icon: <DeleteOutlined />,
        handler: (obj: IContact) => {
          removeHook.select(obj!);
        },
        type: 'destructive',
        canRender: () => {
          if (modalState === ModalState.New || modalState === ModalState.Edit) {
            return false;
          }
          return hasPermissionDelete;
        },
      },
    },
    save: {
      hook: saveHook,
      getDefinition: (onSave?: (obj: IContact) => void) => {
        return {
          key: 'action-save',
          title: 'Save',
          icon: null,
          type: 'primary',
          canRender: () => modalState === ModalState.Edit || modalState === ModalState.New,
          handler: async () => {
            try {
              const values = await saveHook.form.validateFields();
              values.dob = values.dob ? values.dob.format('YYYY-MM-DD') : null;
              const obj = await saveHook.save(values as IContact); // FIXME: The form.validateFields should be typed?
              if (onSave) {
                onSave(obj);
              }
              setModalState(ModalState.Hidden);
              await listHook.reloadList();
            } catch (e) {}
          },
        };
      },
    },
  };
};
